import { Injectable } from '@angular/core';
import { Socket, SocketIoConfig } from 'ngx-socket-io';
import { environment } from '@environments/environment';
import { last, lastValueFrom, Observable, takeUntil, timer } from 'rxjs';
import { SocketEventTypes } from '@enums/socket.enum';
import { BusInfoRegister, ServiceSeatSelect } from '@models/entities/tickets/ticketsData';
import { UpdatePositionBus } from '@models/entities/operation/operation';

const options = {
  timeout: 10000,
  transports: ['polling'],
  autoConnect: false, // Deshabilitamos la conexión automática inicial
  reconnection: true,  // Permitimos la reconexión, pero la controlaremos
  reconnectionAttempts: Infinity, // O un número finito de intentos
  reconnectionDelay: 1000, // Tiempo en milisegundos entre intentos
  reconnectionDelayMax: 5000,
  randomizationFactor: 0.5,
};

@Injectable()
export class SocketProvider extends Socket {

  private shouldReconnect: boolean = true;
  
  constructor(private _socket: Socket) {
    super({
      url: environment.socket_url,
      options: {
        timeout: 10000,
        transports: ['polling'],
        autoConnect: false, // Deshabilitamos la conexión automática inicial
        reconnection: true,  // Permitimos la reconexión, pero la controlaremos
        reconnectionAttempts: Infinity, // O un número finito de intentos
        reconnectionDelay: 1000, // Tiempo en milisegundos entre intentos
        reconnectionDelayMax: 5000,
        randomizationFactor: 0.5,
      }
    });
  }

  initSocket(user: string, copny: any): void {
    if (this.isConnected()) {
      this._socket.disconnect();
    }
    const socketIoConfig: SocketIoConfig = {
      url: environment.socket_url,
      options
    };
    this._socket = new Socket(socketIoConfig);
    this.shouldReconnect = true;
    this._socket.connect();
    this._socket.emit(SocketEventTypes.REGISTER, { user, copny });
    this.reconnect(user, copny);
  }

  reconnect(user: string, copny: any): void {
    if (this.shouldReconnect) {
      this._socket.removeAllListeners('reconnect');
      this._socket.on('reconnect', () => {
        if (this.shouldReconnect) {
          console.log('Reconnect...');
          this._socket.emit(SocketEventTypes.REGISTER, { user, copny });
        }
      });
    }
  }

  onBusUpdated = (callback: any): any => {
    this._socket.removeAllListeners(SocketEventTypes.BUS_UPDATED);
    this._socket.on(SocketEventTypes.BUS_UPDATED, (data: UpdatePositionBus) => callback(data) );
  }

  isConnected(): boolean { return this._socket?.ioSocket?.connected; }
  onDisconnect(): void {
    this.shouldReconnect = false;
    this._socket.disconnect();
    this._socket.removeAllListeners();
  }
}

@Injectable()
export class SocketTicketProvider extends Socket {

  private shouldReconnect: boolean = true;

  constructor(private _socket: Socket) {
    super({
      url: environment.socket_ticket_url,
      options: {
        timeout: 10000,
        transports: ['polling'],
        autoConnect: false, // Deshabilitamos la conexión automática inicial
        reconnection: true,  // Permitimos la reconexión, pero la controlaremos
        reconnectionAttempts: Infinity, // O un número finito de intentos
        reconnectionDelay: 1000, // Tiempo en milisegundos entre intentos
        reconnectionDelayMax: 5000,
        randomizationFactor: 0.5,
      }
    });
  }

  // tslint:disable-next-line: variable-name
  initSocketTikets(user: any, role: any, origin: string, destination: string, busInfoRegister: BusInfoRegister, isreconnection?: boolean): void {
    if (this.isConnected()) {
      this._socket.disconnect();
    }
    const socketIoConfig: SocketIoConfig = {
      url: environment.socket_ticket_url,
      options
    };
    this._socket = new Socket(socketIoConfig);
    this.shouldReconnect = true;
    this._socket.connect();
    this._socket.emit(SocketEventTypes.REGISTER, {user, role, origin, destination, busInfoRegister, platform: 'DES',  isreconnection});
    this.reconnect(user, role, origin, destination, busInfoRegister, 'DES', isreconnection);
  }

  _getselectedSeats = () => {
    this._socket.emit(SocketEventTypes.GET_SELECTED_SEAT);
  }

  getSeatsFromOtherUsers = (callback: any): any => {
    this._socket.removeAllListeners(SocketEventTypes.SELECTED_SEATS);
    this._socket.on(SocketEventTypes.SELECTED_SEATS, (data: any) => callback(data) );
  }

  emitSendSeat = (seat: number, selected: boolean, service: ServiceSeatSelect): Observable<ServiceSeatSelect> => {
    return new Observable((subscriber) => {
       this._socket.emit(SocketEventTypes.SEND_SEAT, { ...service, seat, selected },
        (confirmation: ServiceSeatSelect) => {
          subscriber.next(confirmation);
          subscriber.complete();
       });
    });
  }

  async selectMySeats(seat: number, selected: boolean, service: ServiceSeatSelect): Promise<ServiceSeatSelect> {
    this._socket.removeAllListeners(SocketEventTypes.SEND_SEAT);
    const seatInfo$ = this.emitSendSeat(seat, selected, service).pipe(
      takeUntil(timer(30000)),
      last()
    );

    return await lastValueFrom(seatInfo$);
  }

  selectedStanding = (callback: any): any => {
    this._socket.removeAllListeners(SocketEventTypes.SELECTED_STANDDING);
    this._socket.on(SocketEventTypes.SELECTED_STANDDING, (data: any) => callback(data) );
  }

  selectedWheelchair = (callback: any): any => {
    this._socket.removeAllListeners(SocketEventTypes.SELECTED_WHEELCHAIR);
    this._socket.on(SocketEventTypes.SELECTED_WHEELCHAIR, (data: any) => callback(data) );
  }

  selectedWithoutNumber = (callback: any): any => {
    this._socket.removeAllListeners(SocketEventTypes.SELECTED_WITHOUT_NUMBER);
    this._socket.on(SocketEventTypes.SELECTED_WITHOUT_NUMBER, (data: any) => callback(data) );
  }

  isFullIsComp(): any {
    this._socket.removeAllListeners('availability-change');
    return new Promise(resolve => {
      this._socket.on('availability-change', (data: any) => { resolve(data); });
    });
  }

  recieveMessage = (callback): any => {
    this._socket.removeAllListeners(SocketEventTypes.ERROR);
    this._socket.on(SocketEventTypes.ERROR, (data: any) => callback(data) );
  }

  recieveMessageTime(): any {
    this._socket.removeAllListeners(SocketEventTypes.TIME_EXPIRED);
    return new Promise(resolve => {
      this._socket.on(SocketEventTypes.TIME_EXPIRED, (data: any) => { resolve(data); });
    });
  }

  purchaseDone(): void {
    this._socket.emit(SocketEventTypes.PURCHASE_DONE);
  }

  disconnect(): any {
    this._socket.removeAllListeners(SocketEventTypes.DISCONNECT);
    return new Promise(resolve => {
      this._socket.on(SocketEventTypes.DISCONNECT, (data: any) => { resolve(data); });
    });
  }

  onDisconnect(): void {
    this.shouldReconnect = false;
    this._socket.disconnect();
    this._socket.removeAllListeners();
  }

  onConnect(): void { this._socket.connect(); }
  isConnected(): boolean { return this._socket?.ioSocket?.connected; }

  // tslint:disable-next-line: variable-name
  reconnect(user: any, role: any, origin: string, destination: string, busInfoRegister: BusInfoRegister, platform, isreconnection: boolean): void {
    if (this.shouldReconnect) {
      this._socket.removeAllListeners('reconnect');
      this._socket.on('reconnect', () => {
        if (this.shouldReconnect) {
          console.log('Reconnect...');
          this._socket.emit(SocketEventTypes.REGISTER, { user, role, origin, destination, busInfoRegister, platform, isreconnection});
        }
      });
    }
  }
}

@Injectable()
export class SocketPrintProvider extends Socket {
  constructor(private _socket: Socket) {
    super({
      url: environment.socket_print_url,
      options: {
        reconnection: true,
        timeout: 10000,
        transports: ['polling']
      }
    });
  }

  print(data: any): void {
    if (this._socket) this._socket.disconnect();
    const socketIoConfig: SocketIoConfig = {
      url: environment.socket_print_url,
      options: {
        reconnection: true,
        timeout: 10000,
        transports: ['polling']
      }
    };
    this._socket = new Socket(socketIoConfig);
    this._socket.connect();
    this._socket.emit('print', data);
  }

  isConnected(): boolean { return this._socket && this._socket.ioSocket.connected; }
  onConnect(): void { this._socket.connect(); }
  onDisconnect(): void { this._socket.disconnect(); }
}

@Injectable()
export class PasserPrintingAgentProvider {
  private socket: WebSocket;
  private url = 'ws://localhost:8443';
  private reconnectInterval = 5000;

  constructor() {
    this.connect();
  }

  private connect(): void {
    console.log(`Connecting to Printing Agent at ${this.url}...`);
    this.socket = new WebSocket(this.url);

    this.socket.onopen = () => {
      console.log('Connected to Printing Agent');
      this.authenticate();
    };

    this.socket.onmessage = (event) => {
      console.log('Message from Printing Agent:', event.data);
    };

    this.socket.onclose = () => {
      console.log('Disconnected from Printing Agent. Reconnecting...');
      setTimeout(() => this.connect(), this.reconnectInterval);
    };

    this.socket.onerror = (error) => {
      console.error('WebSocket Error:', error);
      this.socket.close();
    };
  }

  private authenticate(): void {
    const authMessage = {
      type: 'auth',
      data: { id_token: 'local-diagnostic-session' }
    };
    this.sendRaw(authMessage);
  }

  private sendRaw(message: any): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    } else {
      console.error('WebSocket is not open. Cannot send message:', message);
    }
  }

  print(payload: any): void {
    this.sendRaw({
      type: 'engine:print',
      data: payload
    });
  }

  // Backwards compatibility or alternative name if needed
  send(data: any): void {
    this.print(data);
  }

  isConnected(): boolean {
    return this.socket && this.socket.readyState === WebSocket.OPEN;
  }
}
